/** The Market Outlook content (screens design §2.3), shared by the page and the drawer: rates, economy, property and the loan-defaults and market-growth table, on one horizon switch. */
import { GxNotes } from "../theme/GxNotes";
import { useState, type CSSProperties, type ReactNode } from "react";
import type { ContentCatalog } from "@bsim/contracts";
import type { MarketOutlook } from "@bsim/domain";
import { GxDataTable } from "../financials/DataTable";
import { ChartTablesProvider } from "../theme/ChartFrame";
import { GxLineChart } from "../theme/LineChart";
import { GxSegmented } from "../theme/Segmented";
import { useElementWidth } from "../theme/useElementWidth";
import { pauseSignalOf, pauseWords, type PauseSetting } from "./pauseSignal";
import { BOOKS, HORIZONS, SECTIONS, cellOf, chartOf, soWhatLine, type Horizon, type SeriesId } from "./outlook";
import "./outlook.css";

const HEIGHT = 190;
const DESIGN_WIDTH = 280;

function OutlookChart({ id, outlook, horizon, catalog }: { readonly id: SeriesId; readonly outlook: MarketOutlook; readonly horizon: Horizon; readonly catalog: ContentCatalog }) {
  const [ref, width] = useElementWidth<HTMLDivElement>(DESIGN_WIDTH);
  const series = outlook.series.find((s) => s.id === id);
  if (series === undefined) return null;
  const m = chartOf(series, outlook, horizon, catalog);
  // Quarter labels are wide: Start, Today, the last quarter and each year-end are named (the last is always drawn); the quarters between stay on the table.
  const soWhat = soWhatLine(m);
  const labelled = (i: number) => i === 0 || i === m.todayAt || /^Q4\b/.test(m.categories[i] ?? "");
  return (
    <figure className="gx-outlook-fig" ref={ref} data-testid={`outlook.chart.${id}`}>
      <figcaption className="gx-outlook-cap">{m.title}</figcaption>
      <p className="gx-small gx-faint gx-outlook-latest" data-testid={`outlook.chart.${id}.latest`}>Latest actual {m.formatEnd(m.values[m.todayAt] ?? 0)}</p>
      <GxLineChart
        width={width} height={HEIGHT} testId={`outlook.svg.${id}`}
        categories={m.categories}
        series={[{ key: id, color: "var(--c1)", labelColor: "var(--c1)", values: m.values, dashFrom: m.todayAt, name: m.title }]}
        {...(m.range === null ? {} : { range: { ...m.range, color: "var(--c1)" } })}
        rule={{ at: m.todayAt, label: "" }}
        labelled={labelled}
        thresholds={[]} yTicks={m.yTicks} formatY={m.formatY} formatEnd={m.formatEnd}
        summary={m.summary} table={m.table}
      />
      {soWhat === null ? null : <p className="gx-small gx-outlook-sowhat" data-testid={`outlook.chart.${id}.sowhat`}>{soWhat}</p>}
    </figure>
  );
}

/** What the three legend entries mean, said once under the first chart. */
const LEGEND_NOTE = "Realised is what has happened. Consensus is the consensus forecast from today on. The likely range is the spread around the consensus that outcomes usually stay inside.";

function Section({ title, children, testId, note, columns }: { readonly title: string; readonly children: ReactNode; readonly testId: string; readonly note?: string; readonly columns: number }) {
  return (
    <section className="gx-card gx-outlook-card" data-testid={testId}>
      <div className="gx-card-h"><h2 className="gx-h3">{title}</h2></div>
      <div className="gx-card-b gx-outlook-grid" style={{ "--cols": columns } as CSSProperties}>{children}</div>
      {note === undefined ? null : <div className="gx-card-b"><GxNotes testId={`${testId}.legend`} notes={[note]} /></div>}
    </section>
  );
}

export function OutlookBody({ outlook, catalog, strip, narrow = false, pauseSetting }: {
  readonly outlook: MarketOutlook; readonly catalog: ContentCatalog; readonly strip?: ReactNode; readonly narrow?: boolean; readonly pauseSetting?: PauseSetting;
}) {
  // The drawer is opened while deciding the coming year (one round, 12 months); the Outlook page shows the long view.
  const [horizon, setHorizon] = useState<Horizon>(narrow ? 12 : 36);
  const signal = pauseSignalOf(outlook);
  // One switch for the whole page: every chart reads as its table, or none does.
  const [asTable, setAsTable] = useState(false);
  const byId = (id: SeriesId) => outlook.series.find((s) => s.id === id);
  const rows = [
    ...BOOKS.map((b) => ({ id: b.key, label: b.label, cells: [cellOf(byId(`loanDemand.${b.key}` as SeriesId), outlook, horizon, true), cellOf(byId(`defaultRate.${b.key}` as SeriesId), outlook, horizon, false)] })),
    { id: "deposits", label: "Deposit market growth", cells: [cellOf(byId("depositMarketGrowth"), outlook, horizon, true), null] },
  ];
  // One elasticity set drives every loan book, so the five demand figures are the same by design (T19): said once, in one line, when they are identical.
  const demand = rows.flatMap((r) => (r.id === "deposits" ? [] : [r.cells[0] ?? null]));
  const sameDemand = demand.length > 1 && demand[0] !== null && demand.every((d) => d === demand[0]);
  const shownRows = sameDemand ? rows.map((r) => (r.id === "deposits" ? { ...r, cells: [null, r.cells[1] ?? null] } : { ...r, cells: [r.cells[1] ?? null] })) : rows;
  return (
    <div className={`gx-outlook${narrow ? " gx-outlook--narrow" : ""}`}>
      {strip}
      {signal === null || !signal.paused ? null : <p className="gx-outlook-callout" role="status" data-testid="outlook.pauseSignal">{pauseWords(signal, pauseSetting)}</p>}
      <div className="gx-outlook-bar">
        <span className="gx-eyebrow gx-outlook-hl" aria-hidden="true">Horizon</span>
        <GxSegmented label="Months ahead" options={HORIZONS.map((h) => ({ value: h, label: "months" }))} value={horizon} onChange={setHorizon} testId="outlook.horizon" />
        <ul className="gx-outlook-legend gx-caption gx-muted" aria-label="How to read the charts">
          <li><i className="gx-outlook-sw gx-outlook-sw--solid" />Realised</li>
          <li><i className="gx-outlook-sw gx-outlook-sw--dash" />Consensus</li>
          <li><i className="gx-outlook-sw gx-outlook-sw--band" />Likely range</li>
        </ul>
        <button type="button" className="gx-btn gx-btn--secondary" aria-pressed={asTable} data-testid="outlook.astables" onClick={() => setAsTable(!asTable)}>{asTable ? "Show as charts" : "Show as tables"}</button>
      </div>
      <ChartTablesProvider asTable={asTable}>
      {SECTIONS.map((sec, i) => (
        <Section key={sec.key} title={sec.title} testId={`outlook.${sec.key}`} columns={sec.ids.length === 4 ? 4 : 3} {...(i === 0 ? { note: LEGEND_NOTE } : {})}>
          {sec.ids.map((id) => <OutlookChart key={id} id={id} outlook={outlook} horizon={horizon} catalog={catalog} />)}
        </Section>
      ))}
      </ChartTablesProvider>
      <section className="gx-card gx-outlook-card gx-outlook-markets" data-testid="outlook.markets">
        <div className="gx-card-h"><h2 className="gx-h3">{sameDemand ? "Loan defaults and market growth" : "Loan demand and defaults"}</h2><span className="gx-caption gx-muted">Consensus {horizon} months ahead{rows.some((r) => r.cells.some((c) => c?.includes("("))) ? ", likely range in brackets" : ""}</span></div>
        <div className="gx-card-b">
          {sameDemand ? <p className="gx-small gx-num" data-testid="outlook.markets.demand">Loan demand growth, all loan types: <b>{demand[0]}</b>{rows.find((r) => r.id === "deposits")?.cells[0] == null ? null : <> · Deposit market growth: <b>{rows.find((r) => r.id === "deposits")?.cells[0]}</b></>}</p> : null}
          <GxDataTable testId="outlook.markets.table" caption={sameDemand ? "Loans defaulting in a year, by book, consensus with range" : "Loan demand growth and loans defaulting in a year, by book, consensus with range"} firstColumn="Market"
            columns={sameDemand ? [`Loans defaulting in a year, ${horizon} months ahead`] : ["Loan demand growth", `Loans defaulting in a year, ${horizon} months ahead`]} rows={sameDemand ? shownRows.flatMap((r) => (r.id === "deposits" ? [] : [r])) : rows} />
        </div>
      </section>
    </div>
  );
}
